/* eslint-disable camelcase */
// @noflow
import {
  filter,
  find,
  first,
  forEach,
  get,
  isEmpty,
  isEqual,
  map,
  orderBy,
  toLower,
  unionBy,
  uniqBy,
} from 'lodash';
import OnDemandEventListing from './OnDemandEventListing';
import DvrEventListing from './DvrEventListing';
import AiringEventListing from './AiringEventListing';
import PurchaseEventListing from './PurchaseEventListing';
import Stream from './stream';
import type { Franchise as FranchiseJson } from '../franchise/types';

export const SourceType = {
  EST: 'est',
  ON_DEMAND: 'on_demand',
  TRAILER: 'trailer',
  SLING_DVR: 'dvr',
  SLING_LIVE: 'live',
  LIVE_LINEAR: 'live_linear',
  NONE: 'none',
};

export const isNonSlingSourceType = (source: SourceType) =>
  source === SourceType.ON_DEMAND || source === SourceType.LIVE_LINEAR || source === SourceType.EST;

export const isSlingSourceType = (source: SourceType): boolean =>
  source === SourceType.SLING_DVR || source === SourceType.SLING_LIVE;

export const isLiveLinearSourceType = (source: SourceType): boolean =>
  source === SourceType.LIVE_LINEAR;

export const isSlingLiveSourceType = (source: SourceType): boolean =>
  source === SourceType.SLING_LIVE;

export const isLiveSourceType = (source: SourceType): boolean =>
  isLiveLinearSourceType(source) || isSlingLiveSourceType(source);

export const isDvrSourceType = (source: SourceType): boolean => source === SourceType.SLING_DVR;

export default class Franchise {
  static isLocked(franchise: FranchiseJson) {
    if ((franchise.is_subscription || franchise.is_locked) && franchise.player_type !== 'hulu') {
      return true;
    }

    return false;
  }

  modelType = 'Franchise';
  _franchise: FranchiseJson;

  constructor(franchise: FranchiseJson | Franchise) {
    this._franchise = franchise instanceof Franchise ? franchise._franchise : franchise;
  }

  get airings() {
    return filter(this._franchise.airings, airing => airing.airing_type !== 'linear').map(
      airing => new AiringEventListing(airing, this._franchise),
    );
  }

  get episodes() {
    return map(
      this._franchise.episodes,
      episode => new OnDemandEventListing(episode, this._franchise),
    );
  }

  get availableSeasons() {
    const seasons = Object.keys(this.episodesBySeason);
    return seasons;
  }

  get defaultSeason() {
    const defaultSeason =
      this.availableSeasons[this.availableSeasons.length - 1] !== 'N/A'
        ? this.availableSeasons[this.availableSeasons.length - 1]
        : this.availableSeasons[this.availableSeasons.length - 2]
        ? this.availableSeasons[this.availableSeasons.length - 2]
        : first(this.availableSeasons);
    return defaultSeason;
  }

  get episodesBySeason() {
    const episodesBySeason = {};

    forEach(this._franchise.episode_seasons, (episodes, season) => {
      episodesBySeason[season] = map(
        episodes,
        episode => new OnDemandEventListing(episode, this._franchise),
      );
    });

    const recordingSeasonKeys = Object.keys(this.recordingsBySeason);

    map(recordingSeasonKeys, season => {
      episodesBySeason[season] = unionBy(
        episodesBySeason[season],
        this.recordingsBySeason[season],
        'episodeNumber',
      );
    });

    if (episodesBySeason[0]) {
      forEach(this.recordings, recording => {
        const { season, episodeNumber } = recording;

        if (
          (season === 0 || season === 'N/A') &&
          (episodeNumber === 0 || episodeNumber === 'N/A') &&
          !find(episodesBySeason[0], ['id', recording.id])
        ) {
          episodesBySeason[0].push(recording);
        }
      });
    }

    const seasonKeys = Object.keys(episodesBySeason);

    seasonKeys.forEach(season => {
      episodesBySeason[season] = orderBy(episodesBySeason[season], ['episodeNumber'], ['desc']);
    });

    return episodesBySeason;
  }

  get recordings() {
    return map(
      this._franchise.recordings,
      recording => new DvrEventListing(recording, this._franchise),
    );
  }

  get availableDvrSeasons() {
    const { recording_seasons: recordingsBySeason } = this._franchise;

    return recordingsBySeason ? Object.keys(recordingsBySeason) : [];
  }

  get uniqueRecordings() {
    return uniqBy(this.recordings, recording => {
      const { season, episodeNumber } = recording;

      if (season !== 0 && season !== 'N/A' && episodeNumber !== 0 && episodeNumber !== 'N/A') {
        return [season, episodeNumber].join();
      }
      return recording;
    });
  }

  get recordingsBySeason() {
    const { recording_seasons: recordingsBySeason } = this._franchise;
    const seasons = recordingsBySeason ? Object.keys(recordingsBySeason) : null;
    const populatedRecordingsBySeason = {};

    if (seasons) {
      seasons.map(season => {
        if (!populatedRecordingsBySeason[season]) {
          populatedRecordingsBySeason[season] = [];
        }
        recordingsBySeason[season].map(recording => {
          const match = this.uniqueRecordings.filter(item => recording === item.id)[0];
          if (match) {
            populatedRecordingsBySeason[season].push(match);
          }
        });
      });
    }

    return populatedRecordingsBySeason;
  }

  get defaultDvrSeason() {
    const dvrSeasons = this.availableDvrSeasons;
    const defaultSeason =
      dvrSeasons[dvrSeasons.length - 1] !== 'N/A'
        ? dvrSeasons[dvrSeasons.length - 1]
        : dvrSeasons[dvrSeasons.length - 2]
        ? dvrSeasons[dvrSeasons.length - 2]
        : first(dvrSeasons);
    return defaultSeason;
  }

  findRecordingsByListing(listing = this.nextStream): Array<BaseListing> {
    const listings = [];

    if (!isEmpty(listing)) {
      if (this.isShow) {
        listings.push(
          ...filter(this.recordings, episode => {
            if (listing.episodeNumber === 0 || listing.episodeNumber === 'N/A') {
              return episode.id === listing.id;
            }
            return (
              episode.episodeNumber === listing.episodeNumber && episode.season === listing.season
            );
          }),
        );
      } else {
        listings.push(...filter(this.mergedListings, listing => listing.watchNow));
      }
    }

    return listings;
  }

  get dynaStreams() {
    return map(this._franchise.dyna_streams, stream => new Stream(stream, this._franchise));
  }

  get purchases() {
    const purchases = [
      ...map(
        this._franchise.purchases,
        purchase => new PurchaseEventListing(purchase, this._franchise),
      ),
    ];

    forEach(this.episodes, episode => {
      purchases.push(
        ...map(episode.purchases, purchase => new PurchaseEventListing(purchase, this._franchise)),
      );
    });

    return purchases;
  }

  /**
   * Get all listings ordered by rank
   */
  get mergedListings() {
    return orderBy(
      [
        ...this.purchases,
        ...this.episodes,
        ...this.dynaStreams,
        ...this.recordings,
        ...this.airings,
      ],
      ['season', 'episodeNumber'],
      ['desc', 'desc'],
    );
  }

  get description() {
    return this._franchise.description;
  }

  get posterImage() {
    return !isEmpty(this._franchise.images?.poster_url)
      ? this._franchise.images.poster_url.replace(/^http:/, 'https:')
      : undefined;
  }

  get backdropImage() {
    const { wide_poster_url: widePosterUrl, background_url: backgroundUrl } =
      this._franchise.images || {};
    if (!isEmpty(backgroundUrl)) {
      return backgroundUrl;
    }
    return !isEmpty(widePosterUrl) ? widePosterUrl.replace(/rs\/(\d)+/i, 'rs/1280') : null;
  }

  get networks() {
    return this._franchise.networks || [];
  }

  get franchiseName() {
    return this._franchise.name;
  }

  get contentId() {
    return this._franchise.content_identification?.content_id;
  }

  get content_identification() {
    return this._franchise.content_identification;
  }

  get kind() {
    return this._franchise.kind;
  }

  get genres() {
    return this._franchise.genres;
  }

  get trailers() {
    return this._franchise.trailers || [];
  }

  get castAndCrew() {
    return this._franchise.cast_and_crew || [];
  }

  get clips() {
    if (
      this._franchise.clips &&
      this._franchise.clips.length &&
      this._franchise.content_identification
    ) {
      this._franchise.clips[0].content_identification = this._franchise.content_identification;
      return this._franchise.clips || [];
    }
    return undefined;
  }

  get contentType() {
    // eslint-disable-next-line camelcase
    return this._franchise.content_identification?.content_type;
  }

  get slug() {
    return this._franchise.slug;
  }

  get hasOnDemandContent() {
    return !isEmpty(this.episodes) || !isEmpty(this.dynaStreams);
  }

  get isLocked() {
    return this._franchise.is_locked;
  }

  get isParentalLocked() {
    return this._franchise.is_parental_locked;
  }

  get purchasable() {
    return this._franchise.purchasable;
  }

  get purchased() {
    return !isEmpty(this.purchases);
  }

  get echostar3Id() {
    return this._franchise.echostar3_id;
  }

  get pricingPlanId() {
    return this._franchise.pricing_plan_id;
  }

  get productId() {
    return this._franchise.product_id;
  }

  get rating() {
    return this._franchise.rating;
  }

  get rottenTomatoes() {
    return this._franchise.tomatometer;
  }

  get hasData() {
    return !!Object.keys(this._franchise).length;
  }

  get releaseYear() {
    return this._franchise.release_year;
  }

  // in seconds
  get runtime() {
    if (this._franchise.run_time <= 0 && this.kind === 'movie') {
      const { dyna_streams: dynaStreams } = this._franchise;
      if (!isEmpty(dynaStreams)) {
        return dynaStreams[0].run_time || this._franchise.run_time;
      }
    }
    return this._franchise.run_time;
  }

  get formattedRuntime() {
    const { runtime } = this;
    if (runtime == null || Number.isNaN(runtime) || runtime <= 0) return '';
    const runtimeInMinutes = Math.round(runtime / 60);
    const hours = Math.floor(runtimeInMinutes / 60);
    const minutes = Math.floor(runtimeInMinutes % 60);
    if (runtimeInMinutes > 60) {
      return `${hours}h ${minutes}m`;
    }
    if (hours < 1) {
      return `${minutes}m`;
    }
    return `${hours}h`;
  }

  get isShow(): boolean {
    return toLower(this._franchise.kind) === 'show';
  }

  get isMovie(): boolean {
    return toLower(this._franchise.kind) === 'movie';
  }

  get channel(): ?string {
    if (toLower(this._franchise.kind) === 'movie') {
      return this._airings && this._airings[0].channelInformation;
    }
    return get(this.episodes, '0.channel');
  }

  get channelImage(): ?string {
    return get(this.episodes, '0.channelImage');
  }

  get onNow(): boolean {
    return !!this.airings.find(airing => airing.onNow);
  }

  get commonSenseAge(): number {
    return this._franchise.common_sense_data && this._franchise.common_sense_data.slider
      ? this._franchise.common_sense_data.slider.target_age
      : 0;
  }

  get hasSeasons(): boolean {
    return (
      !this.isMovie &&
      this.mergedListings.find(listing => listing.season && listing.season !== 'N/A')
    );
  }

  /**
   * Gets the next available stream for this franchise
   *
   * 1. Check user watched history for episodes
   *  - Resume playback of last watched unfinished episode
   *  - Next unwatched episode in season/episode order
   * 2. Determine best playback stream type for user
   *  - EST > VOD > DVR > On Now Live Airing
   *
   * @returns {BaseListing} Next available stream
   */
  get nextStream(): BaseListing {
    const lastPage = process.env.IS_BROWSER && window.location.href;
    if (this.purchases.length && !lastPage) {
      return this.mergedListings.find(listing => listing.modelType === 'PurchaseEventListing');
    }
    if (this.episodes.length && !lastPage) {
      return this.mergedListings.find(
        listing =>
          listing.watchNow && !listing.isLocked && listing.modelType === 'OnDemandEventListing',
      );
    }

    if (lastPage && lastPage.includes('network=')) {
      const networkName = lastPage.split('network=')[1].replace(/%20/g, ' ');
      return this.mergedListings.find(
        listing =>
          listing.watchNow &&
          !listing.isLocked &&
          (listing.networkName || listing._listing.network) === networkName,
      );
    }

    return this.mergedListings.find(listing => listing.watchNow && !listing.isLocked);
  }

  get nextStreamForKids(): BaseListing {
    return this.mergedListings.find(
      listing =>
        listing.watchNow && !listing.isLocked && listing.modelType !== 'AiringEventListing',
    );
  }

  getNextStreamByNetworkId(networkId) {
    return this.mergedListings.find(
      listing =>
        listing.watchNow &&
        !listing.isLocked &&
        (listing.networkId ||
          (listing._listing && listing._listing.network_id) ||
          (listing._stream && listing._stream.network_id)) === networkId,
    );
  }

  getNextStreamByContentId(contentId) {
    return this.mergedListings.find(
      listing =>
        listing.watchNow &&
        !listing.isLocked &&
        (listing.contentId ||
          (listing._listing && listing._listing.contentId) ||
          (listing._stream && listing._stream.contentId)) === contentId,
    );
  }

  getNextStreamByLocation(location) {
    if (location === 'dvr') {
      return this.recordings[0];
    }
    return this.nextStream;
  }

  listingHasMoreWaysToWatch(listing = this.nextStream) {
    return this.moreWaysToWatch(listing).length > 1;
  }

  moreWaysToWatch(listing = this.nextStream): Array<BaseListing> {
    const listings = [];

    if (!isEmpty(listing)) {
      if (this.isShow) {
        listings.push(
          ...filter(this.mergedListings, episode => {
            if (!episode.watchNow) {
              return false;
            }

            return (
              episode.episodeNumber === listing.episodeNumber &&
              episode.season === listing.season &&
              listing.episodeNumber !== 0 &&
              listing.episodeNumber !== 'N/A' &&
              listing.season !== 0 &&
              listing.season !== 'N/A'
            );
          }),
        );
      } else {
        listings.push(...filter(this.mergedListings, listing => listing.watchNow));
      }
    }
    return listings;
  }

  /**
   * Returns true if the franchise has a stream ready for the user to immediately start playing.
   * For mobile Franchise page, the main franchise image will have a clicable play button when true.
   *
   * @returns {Boolean} Has next stream
   */
  get watchNow(): boolean {
    return !isEmpty(this.nextStream);
  }

  get full(): boolean {
    return this._franchise.full || false;
  }

  get previewable(): boolean {
    return this._franchise.previewable || this.full;
  }

  get criticsRating(): ?string {
    return this._franchise.critics_rating;
  }

  get seriesId() {
    return this._franchise.content_identification?.series_id;
  }

  getStreamById(ruid?: string, playerType: string = 'html5') {
    const listings = [...this.purchases, ...this.dynaStreams];
    if (!listings.length) {
      return null;
    }
    return listings.find(
      stream => stream.playerType === playerType && (!ruid || stream.ruid === ruid),
    );
  }

  getStreamByPlayerType(playerType: string = 'html5') {
    const listings = [...this.purchases, ...this.dynaStreams];
    if (!listings.length) {
      return null;
    }
    return listings.find(stream => stream.playerType === playerType);
  }

  equals(franchise: FranchiseJson | Franchise) {
    const franchiseJson = franchise instanceof Franchise ? franchise.json() : franchise;

    return isEqual(this._franchise, franchiseJson);
  }

  json() {
    return this._franchise;
  }
}
